import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { TbRefresh } from "react-icons/tb";
import {
  Users,
  ShieldCheck,
  Clock,
  AlertTriangle,
  UserX,
  AlertOctagon,
  Building2,
  CalendarDays,
  GraduationCap,
  Settings2,
} from "lucide-react";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  formatDateOnlyIST,
} from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";
import "./trainingDashboard.css";

function TrainingDashboardPage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardResponse, matrixResponse] =
        await Promise.all([
          hrRecordsApi.getTrainingComplianceDashboard(),
          hrRecordsApi.getTrainingComplianceMatrix({ only_mandatory: true }),
        ]);

      setDashboard(dashboardResponse);
      setMatrix(matrixResponse);
    } catch (error) {
      console.error("Failed to load training dashboard data:", error);
      showSnackbar("Failed to load training dashboard", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totals = dashboard?.totals || {};
  const thisMonth = dashboard?.this_month || {};
  const byStandard = dashboard?.by_standard || [];
  const byTraining = dashboard?.by_training || [];

  const kpiCards = [
    { key: "employees_covered", label: "Employees Covered", value: totals.employees_covered, icon: Users, tone: "blue" },
    { key: "active", label: "Active", value: totals.active, icon: ShieldCheck, tone: "green" },
    { key: "expiring_soon", label: "Expiring Soon", value: totals.expiring_soon, icon: Clock, tone: "amber" },
    { key: "expired", label: "Expired", value: totals.expired, icon: AlertTriangle, tone: "red" },
    { key: "not_trained", label: "Not Trained", value: totals.not_trained, icon: UserX, tone: "slate" },
    { key: "overdue", label: "Overdue", value: totals.overdue, icon: AlertOctagon, tone: "red" },
  ];

  const statStripItems = [
    { label: "Total Records", value: totals.total_records },
    { label: "Due in 30 Days", value: totals.due_in_30 },
    { label: "Due in 60 Days", value: totals.due_in_60 },
    { label: "Due in 90 Days", value: totals.due_in_90 },
    { label: "No Expiry", value: totals.no_expiry },
    { label: "Training Sessions This Month", value: thisMonth.sessions_this_month },
    { label: "Participants This Month", value: thisMonth.participants_this_month },
  ];

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Training Dashboard</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                {/* <Tooltip title="Reminder Settings" arrow>
                  <button
                    type="button"
                    className="new-button htd-settings-button"
                    onClick={() => navigate("/training/reminders")}
                  >
                    <Settings2 size={15} /> Settings
                  </button>
                </Tooltip> */}
                <Tooltip title="Refresh" arrow>
                  <button className="refresh-button" onClick={fetchData}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body hr-records-dashboard-scroll">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : (
                <div className="htd-dashboard">
                  {dashboard && (
                    <>
                      <div className="htd-kpi-grid">
                        {kpiCards.map(({ key, label, value, icon: Icon, tone }) => (
                          <div className={`htd-kpi-card htd-tone-${tone}`} key={key}>
                            <div className="htd-kpi-card-top">
                              <div className="htd-kpi-icon">
                                <Icon />
                              </div>
                            </div>
                            <div className="htd-kpi-value">{value || 0}</div>
                            <div className="htd-kpi-label">{label}</div>
                          </div>
                        ))}
                      </div>

                      <div className="htd-stat-strip">
                        {statStripItems.map((item) => (
                          <div className="htd-stat-chip" key={item.label}>
                            <div className="htd-stat-chip-value">
                              {item.value || 0}
                            </div>
                            <div className="htd-stat-chip-label">
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="htd-two-col">
                        <div className="htd-panel">
                          <div className="htd-panel-header">
                            <Building2 />
                            <div className="htd-panel-title">
                              Compliance by Standard Body
                            </div>
                          </div>
                          {byStandard.length ? (
                            <>
                              <div className="htd-standard-list">
                                {byStandard.map((row) => {
                                  const total = row.total || 0;
                                  const active = row.active || 0;
                                  const expiringSoon = row.expiring_soon || 0;
                                  const expired = row.expired || 0;
                                  const other = Math.max(
                                    total - active - expiringSoon - expired,
                                    0,
                                  );
                                  const pct = (n) =>
                                    total > 0 ? (n / total) * 100 : 0;
                                  return (
                                    <div key={row.standard_body}>
                                      <div className="htd-standard-row-head">
                                        <span className="htd-standard-name">
                                          {row.standard_body}
                                        </span>
                                        <span className="htd-standard-total">
                                          {total} record{total === 1 ? "" : "s"}
                                        </span>
                                      </div>
                                      <div
                                        className="htd-bar"
                                        title={`Active: ${active} | Expiring soon: ${expiringSoon} | Expired: ${expired}`}
                                      >
                                        {active > 0 && (
                                          <div
                                            className="htd-bar-seg active"
                                            style={{ width: `${pct(active)}%` }}
                                          />
                                        )}
                                        {expiringSoon > 0 && (
                                          <div
                                            className="htd-bar-seg expiring"
                                            style={{ width: `${pct(expiringSoon)}%` }}
                                          />
                                        )}
                                        {expired > 0 && (
                                          <div
                                            className="htd-bar-seg expired"
                                            style={{ width: `${pct(expired)}%` }}
                                          />
                                        )}
                                        {other > 0 && (
                                          <div
                                            className="htd-bar-seg other"
                                            style={{ width: `${pct(other)}%` }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="htd-legend">
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot active" />
                                  Active
                                </div>
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot expiring" />
                                  Expiring Soon
                                </div>
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot expired" />
                                  Expired
                                </div>
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot other" />
                                  Not Trained / Other
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="hr-records-empty">
                              No standard body data found.
                            </div>
                          )}
                        </div>

                        <div className="htd-panel">
                          <div className="htd-panel-header">
                            <CalendarDays />
                            <div className="htd-panel-title">This Month</div>
                          </div>
                          <div className="htd-this-month">
                            <div className="htd-this-month-item">
                              <span className="htd-this-month-value">
                                {thisMonth.sessions_this_month || 0}
                              </span>
                              <span className="htd-this-month-label">
                                Training Sessions
                              </span>
                            </div>
                            <div className="htd-this-month-item">
                              <span className="htd-this-month-value">
                                {thisMonth.participants_this_month || 0}
                              </span>
                              <span className="htd-this-month-label">
                                Participants Trained
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="hr-records-section">
                    <div className="htd-section-title-row">
                      <GraduationCap />
                      <div className="hr-records-section-title" style={{ marginBottom: 0 }}>
                        Trainings Needing Attention
                      </div>
                    </div>
                    <div className="data-list-table">
                      {byTraining.length ? (
                        <table className="table multilevel-data">
                          <thead>
                            <tr>
                              <th>TRAINING</th>
                              <th>EMPLOYEES</th>
                              <th>TOTAL RECORDS</th>
                              <th>EXPIRED</th>
                              <th>EXPIRING SOON</th>
                              <th>EARLIEST DUE</th>
                            </tr>
                          </thead>
                          <tbody className="data-list-scroll-bodys">
                            {byTraining.map((item) => (
                              <tr key={item.training_name} className="hoverable-row">
                                <td>{item.training_name || "NA"}</td>
                                <td>{item.employees || 0}</td>
                                <td>{item.total || 0}</td>
                                <td>
                                  <span
                                    className={`htd-count-pill ${
                                      item.expired ? "expired" : "zero"
                                    }`}
                                  >
                                    {item.expired || 0}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`htd-count-pill ${
                                      item.expiring_soon ? "expiring" : "zero"
                                    }`}
                                  >
                                    {item.expiring_soon || 0}
                                  </span>
                                </td>
                                <td>{formatDateOnlyIST(item.earliest_due)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="hr-records-empty">No training data found.</div>
                      )}
                    </div>
                  </div>

                  {/* <div className="hr-records-section">
                    <div className="htd-section-title-row">
                      <ShieldCheck />
                      <div className="hr-records-section-title" style={{ marginBottom: 0 }}>
                        Mandatory Training Matrix
                      </div>
                    </div>
                    <div className="hr-records-matrix">
                      {matrix?.employees?.length ? (
                        <table className="table multilevel-data">
                          <thead>
                            <tr>
                              <th>EMPLOYEE</th>
                              {matrix.trainings?.map((training) => (
                                <th key={training.id}>{training.training_name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="data-list-scroll-bodys">
                            {matrix.employees.map((employee) => (
                              <tr
                                key={employee.actual_man_power_id}
                                className="hoverable-row"
                              >
                                <td>
                                  {employee.person_name || "NA"}
                                  {employee.ec_no && (
                                    <>
                                      <br />
                                      <span className="htd-muted-text">
                                        {employee.ec_no}
                                      </span>
                                    </>
                                  )}
                                </td>
                                {employee.trainings?.map((training) => (
                                  <td key={training.training_master_id}>
                                    <span
                                      className="hr-records-pill"
                                      style={getDuePillStyle(training.state)}
                                    >
                                      {(training.state || "NA").replace(/_/g, " ")}
                                    </span>
                                    {training.next_due_date && (
                                      <>
                                        <br />
                                        <span className="htd-muted-text">
                                          {formatDateOnlyIST(training.next_due_date)}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="hr-records-empty">No matrix data found.</div>
                      )}
                    </div>
                  </div> */}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingDashboardPage;
